// src/hooks/useHotkeys.js
//
// Atajos de teclado sin librería. Uso:
//
//   useHotkeys([
//       { keys: 'j',                run: () => ... },            // tecla suelta
//       { keys: 'g t',              run: () => ... },            // secuencia (g, luego t en < 1 s)
//       { keys: 'ctrl+enter',       run: () => ..., enInputs: true }, // también dentro de inputs
//       { keys: 'a',                run: () => ..., when: () => isStaff },
//   ]);
//
// Reglas:
// - Por defecto los atajos NO disparan cuando el foco está en un input, textarea,
//   select o contenteditable (para no pelear con lo que escribe el usuario).
//   `enInputs: true` lo permite (pensado para ctrl+enter en el cuadro de respuesta).
// - `ctrl` cubre también Cmd en Mac. Para caracteres sueltos ('?', '[', ']') no
//   se exige Shift aunque físicamente lo lleven.
// - `when` es una condición extra evaluada al momento (rol, estado, etc.).
import { useEffect, useRef } from 'react';

const SEQ_TIMEOUT_MS = 900;

export function esCampoDeTexto(el) {
    if (!el || el === document.body) return false;
    const tag = (el.tagName || '').toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
    return !!el.isContentEditable;
}

/** Normaliza un KeyboardEvent a 'ctrl+shift+enter', 'g', '?', 'escape', etc. */
export function comboDe(e) {
    const k = e.key || '';
    const esChar = k.length === 1;
    const parts = [];
    if (e.ctrlKey || e.metaKey) parts.push('ctrl');
    if (e.altKey) parts.push('alt');
    if (e.shiftKey && !esChar) parts.push('shift');
    parts.push(esChar ? k.toLowerCase() : k.toLowerCase());
    return parts.join('+');
}

export default function useHotkeys(bindings, { enabled = true } = {}) {
    const ref = useRef(bindings);
    ref.current = bindings;

    useEffect(() => {
        if (!enabled) return undefined;
        let pendiente = null;   // primera tecla de una secuencia ('g')
        let timer = null;

        const aplica = (b, enInput) => (!enInput || b.enInputs) && (!b.when || b.when());

        const onKey = (e) => {
            if (e.defaultPrevented || e.isComposing) return;
            const combo   = comboDe(e);
            const enInput = esCampoDeTexto(e.target);
            const lista   = ref.current || [];

            if (pendiente) {
                const seq = `${pendiente} ${combo}`;
                pendiente = null;
                clearTimeout(timer);
                const b = lista.find(x => x.keys === seq && aplica(x, enInput));
                if (b) { e.preventDefault(); b.run(e); return; }
            }

            const directo = lista.find(x => x.keys === combo && aplica(x, enInput));
            if (directo) { e.preventDefault(); directo.run(e); return; }

            if (!enInput && lista.some(x => x.keys.startsWith(`${combo} `) && aplica(x, enInput))) {
                pendiente = combo;
                clearTimeout(timer);
                timer = setTimeout(() => { pendiente = null; }, SEQ_TIMEOUT_MS);
            }
        };

        window.addEventListener('keydown', onKey);
        return () => { window.removeEventListener('keydown', onKey); clearTimeout(timer); };
    }, [enabled]);
}
